/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	Fragment,
	type ReactNode,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';

import AvatarGroup from '@atlaskit/avatar-group';
import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import Heading from '@atlaskit/heading';
import CommentIcon from '@atlaskit/icon/core/comment';
import IconTile from '@atlaskit/icon/icon-tile';
import InlineEdit from '@atlaskit/inline-edit';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import TextArea from '@atlaskit/textarea';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import { useProfileResolution } from '@atlassian/conversation-assistant-agent/utils/agents';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import { type ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';
import { useConfluenceEntities } from '@atlassian/conversation-assistant-service/services/confluence/useConfluenceEntities';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';
import { type ConfluencePageOption } from '@atlassian/rovo-platform-ui-components/base-page-selector/types';
import { ConfluencePageSelector } from '@atlassian/rovo-platform-ui-components/confluence-page-selector';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';

import messages from './messages';
/**
 * since react-markdown is esm only, eager loading react-markdown would break jest tests that includes this component
 */
const MarkdownRenderer = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_conversation-assistant-message-actions_react-markdown" */ 'react-markdown'
	).then(({ default: ReactMarkdown }) => ({ default: ReactMarkdown })),
);
const styles = cssMap({
	readView: {
		minHeight: '3.25rem',
		paddingInline: token('space.075'),
		paddingBlock: token('space.100'),
		overflow: 'hidden',
	},

	messageBox: {
		marginTop: token('space.negative.200'),
		marginBottom: token('space.negative.050'),
	},

	pageSelectorWrapper: {
		flexGrow: 1,
	},

	inlineTextSelectionBox: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		width: '100%',
		backgroundColor: token('elevation.surface'),
	},

	inlineTextSelectionHighlighted: {
		backgroundColor: token('color.background.accent.yellow.subtlest'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border.accent.yellow'),
	},

	replyingAsBox: {
		marginTop: token('space.negative.050'),
		marginBottom: token('space.100'),
		marginInline: token('space.100'),
	},
});

export const ConfluencePageCommentAction = (
	props: ActionRendererFor<typeof ActionKeys.ConfluencePageComment>,
): JSX.Element => {
	return <ConfluencePageCommentActionNewLayout {...props} />;
};

const ConfluencePageCommentActionNewLayout = ({
	onCustomDataConfirm,
	actions,
	getStatus,
	getReadonly,
	cloudId,
	sendMessageConfirmedTools,
	recipientAgentId,
	conversationId,
	messageId,
}: ActionRendererFor<typeof ActionKeys.ConfluencePageComment>): JSX.Element => {
	di(useConfluenceEntities, useProfileResolution);
	const { sendAnalyticsEvent } = useAnalytics();
	const { formatMessage } = useIntl();

	const firstAction = actions[0];
	const { data } = firstAction;
	const readonly = getReadonly(firstAction);
	const status = getStatus(firstAction);
	const [selectedEntity, setSelectedEntity] = useState<ConfluencePageOption | undefined>(undefined);
	const [comment, setComment] = useState(data.comment ?? '');
	const { getEntity } = useConfluenceEntities(cloudId);
	const isSubmitting = status === 'started';
	const isActionEnhancementsEnabled = expValEquals(
		'platform_rovo_support_create_inline_comment',
		'isEnabled',
		true,
	);
	const isReply = isActionEnhancementsEnabled && !!data.parentCommentId;
	const isReplyWithAgent = isReply && !!data.systemAccountId;
	const isInlineComment = isActionEnhancementsEnabled && !isReply && !!data.inlineTextSelection;
	const isCommentEmpty = isActionEnhancementsEnabled && (!comment || comment.trim() === '');
	const resolvedContentType = data.contentType === 'blogpost' ? 'blogPost' : data.contentType;
	const fallbackSelectedEntity =
		data.contentId && resolvedContentType
			? {
					label: '',
					value: data.contentId,
					type: resolvedContentType,
				}
			: undefined;
	const submitTargetEntity = selectedEntity ?? (isReply ? fallbackSelectedEntity : undefined);
	const isDisabled = status !== 'idle' || readonly || !submitTargetEntity || !comment;

	useEffect(() => {
		const fetchPage = async () => {
			if (data.contentId) {
				const entity = await getEntity(data.contentId, resolvedContentType);

				if (entity) {
					setSelectedEntity({
						label: entity.name,
						value: entity.id,
						type: resolvedContentType === 'page' ? 'page' : 'blogPost',
					});
				}
			}
		};

		fetchPage();
	}, [data.contentId, resolvedContentType, getEntity]);

	const handleSubmit = useCallback(() => {
		if (
			FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
		) {
			const idleActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: {
					interactionSource: 'confluencePageCommentRenderer',
				},
				confirmed_tools: {
					message_id: messageId,
					tools: idleActions.map((action) =>
						action.invocationId === firstAction.invocationId
							? buildToolFromAction(action, 'confirmed', {
									...data,
									comment,
									contentId: submitTargetEntity?.value,
									contentType: submitTargetEntity?.type ?? 'page',
								})
							: buildToolFromAction(action, 'cancelled'),
					),
				},
			});
			return;
		}

		onCustomDataConfirm(firstAction, {
			...data,
			comment,
			contentId: submitTargetEntity?.value,
			contentType: submitTargetEntity?.type ?? 'page',
		});
	}, [
		onCustomDataConfirm,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		data,
		comment,
		submitTargetEntity?.value,
		submitTargetEntity?.type,
		firstAction,
		actions,
		getStatus,
	]);

	const currentUser = useProfileController();
	const { profile: systemProfile } = useProfileResolution(
		data.systemAccountId ?? '',
		cloudId,
		/* shouldFetch = */ isReplyWithAgent,
	);
	const systemUser = useMemo(() => {
		return {
			displayName: systemProfile.data?.nickname ?? systemProfile.data?.fullName ?? '',
			avatarUrl: systemProfile.data?.avatarUrl ?? '',
		};
	}, [systemProfile.data]);

	const getActionCardTitleMessage = useCallback(() => {
		if (isReply) {
			return messages.confluenceCommentCreateReplyActionCardTitle;
		}
		if (isInlineComment) {
			return messages.confluenceCommentCreateInlineActionCardTitle;
		}
		return messages.confluenceCommentCreateActionCardTitle;
	}, [isReply, isInlineComment]);

	return (
		<Stack space="space.250">
			<Text>
				{formatMessage(messages.actionMessage, {
					name: selectedEntity?.label ?? '',
					bold: (chunks: ReactNode[]) => <Text weight="bold">{chunks}</Text>,
				})}
			</Text>
			<ActionCardWrapper
				title={formatMessage(getActionCardTitleMessage())}
				product="confluence"
				headerIcon={
					isActionEnhancementsEnabled ? (
						<IconTile
							icon={CommentIcon}
							label=""
							testId="confluence-page-comment-header-icon"
							appearance="blue"
							size="medium"
						/>
					) : undefined
				}
				isTitleBold={isActionEnhancementsEnabled}
			>
				<Stack space="space.200">
					{/* Page selector. Hidden if isReplyWithAgent. */}
					{!isReplyWithAgent && (
						<Inline space="space.100" alignBlock="center">
							<Heading size="xsmall">{formatMessage(messages.pageSelectorLabel)}</Heading>

							{!selectedEntity ? (
								<Skeleton height={30} width={200} borderRadius={token('radius.large')} />
							) : (
								<Box xcss={styles.pageSelectorWrapper}>
									<ConfluencePageSelector
										cloudId={cloudId}
										testId="confluence-page-comment-page-selector"
										contentTypes={['page', 'blogPost']}
										// Don't allow changing the page if creating an inline comment with text selection
										isDisabled={isDisabled || isInlineComment || isReply}
										onMenuOpen={() => {
											sendAnalyticsEvent({
												action: 'opened',
												actionSubject: 'dropdown',
												actionSubjectId: 'rovoConfluenceCommentCreatePageSelector',
											});
										}}
										isRequired
										onChange={(option: ConfluencePageOption | null) => {
											if (option) {
												setSelectedEntity(option);
												sendAnalyticsEvent({
													action: 'selected',
													actionSubject: 'dropdown',
													actionSubjectId: 'rovoConfluenceCommentCreatePageSelector',
												});
											}
										}}
										value={selectedEntity}
									/>
								</Box>
							)}
						</Inline>
					)}

					{/* Show selected text when creating a top-level inline comment. */}
					{isInlineComment && (
						<Box testId="inlineTextSelection-container" xcss={styles.inlineTextSelectionBox}>
							<Text size="small" maxLines={3}>
								<span
									data-testId="inlineTextSelection-text"
									css={[!isDisabled && styles.inlineTextSelectionHighlighted]}
								>
									{data.inlineTextSelection}
								</span>
							</Text>
						</Box>
					)}

					{/* Show "replying as an agent with current user" if isReplyWithAgent */}
					{isReplyWithAgent && (
						<Text size="small" weight="bold" color="color.text.subtle">
							{formatMessage(messages.replyingAsLabel)}
						</Text>
					)}
					{isReplyWithAgent && (
						<Box xcss={styles.replyingAsBox}>
							<Inline alignBlock="center" space="space.100">
								<AvatarGroup
									appearance="stack"
									data={[
										{
											appearance: 'hexagon', // use hexagon shape for agent avatar
											name: systemUser.displayName,
											src: systemUser.avatarUrl,
											stackIndex: 0,
											testId: 'replying-as-avatar-agent',
										},
										{
											appearance: 'circle',
											name: currentUser.displayName ?? '',
											src: currentUser.avatarUrl,
											stackIndex: 1,
											testId: 'replying-as-avatar-current-user',
										},
									]}
									size="small"
									maxCount={2}
									testId="replying-as-avatar-group"
								/>
								<Text size="medium">
									{formatMessage(messages.replyWithAgentText, {
										userName: systemUser.displayName || 'Agent',
									})}
								</Text>
							</Inline>
						</Box>
					)}

					{/* Comment Message */}
					{isActionEnhancementsEnabled && (
						<Text size="small" weight="bold" color="color.text.subtle">
							{formatMessage(messages.commentMessageLabel)}
						</Text>
					)}
					<Box xcss={styles.messageBox}>
						{isDisabled ? (
							<Box xcss={styles.readView}>
								<Suspense fallback={<Fragment />}>
									<MarkdownRenderer>{comment}</MarkdownRenderer>
								</Suspense>
							</Box>
						) : (
							<InlineEdit
								defaultValue={comment}
								onConfirm={(value) => {
									setComment(value ?? '');
									sendAnalyticsEvent({
										action: 'edited',
										actionSubject: 'inlineEdit',
										actionSubjectId: 'rovoConfluenceCommentCreateContent',
									});
								}}
								onEdit={() => {
									sendAnalyticsEvent({
										action: 'clicked',
										actionSubject: 'inlineEdit',
										actionSubjectId: 'rovoConfluenceCommentCreateContent',
									});
								}}
								onCancel={() => {
									sendAnalyticsEvent({
										action: 'cancelled',
										actionSubject: 'inlineEdit',
										actionSubjectId: 'rovoConfluenceCommentCreateContent',
									});
								}}
								readView={() => (
									<Box xcss={styles.readView}>
										<Suspense fallback={<Fragment />}>
											<MarkdownRenderer>{comment}</MarkdownRenderer>
										</Suspense>
									</Box>
								)}
								editView={({ errorMessage: _errorMessage, ...fieldProps }, ref) => (
									// @ts-expect-error - TextArea onChange: string | FormEvent but @atlaskit/textarea expects ChangeEvent; upstream type gap
									<TextArea {...fieldProps} isReadOnly={readonly} ref={ref} />
								)}
								isRequired
								hideActionButtons={readonly}
								readViewFitContainerWidth
							/>
						)}
					</Box>
				</Stack>

				<ActionCardFooter>
					<Inline space="space.100" alignInline="end">
						<Button
							appearance="primary"
							isLoading={isSubmitting}
							isDisabled={isDisabled || isCommentEmpty}
							onClick={(_, e) => {
								handleSubmit();

								sendAnalyticsEvent(
									e.update({
										actionSubjectId: 'rovoConfluenceCommentCreateSubmit',
									}),
								);
							}}
						>
							{formatMessage(isReply ? messages.replyButton : messages.publishButton)}
						</Button>
					</Inline>
				</ActionCardFooter>
			</ActionCardWrapper>
		</Stack>
	);
};
